import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import { Eye, EyeOff, LogIn, AlertCircle, Lock, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, getPersonaLanding, clearMustChangePassword } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Password change state (for first-time login)
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    console.log('[LoginPage] Login result:', result);
    
    if (result.success) {
      // Check if user must change password (first-time login with temp password)
      console.log('[LoginPage] must_change_password:', result.must_change_password);
      if (result.must_change_password) {
        console.log('[LoginPage] Showing password change modal');
        setShowPasswordChange(true);
        setLoading(false);
        return;
      }
      
      // Navigate to persona-based landing
      const landing = getPersonaLanding();
      console.log('[LoginPage] Navigating to:', landing);
      navigate(landing, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await api.changePassword({
        current_password: password,
        new_password: newPassword
      });
      
      toast.success('Password changed successfully!');
      
      // Clear the must change password flag in auth context
      clearMustChangePassword();
      
      // Navigate to app
      const landing = getPersonaLanding();
      navigate(landing, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to change password');
    }
    
    setChangingPassword(false);
  };

  // Password Change Modal
  if (showPasswordChange) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--clr-brand-primary)] rounded-2xl mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Change Your Password</h1>
            <p className="text-blue-200">Please set a new password for your account</p>
          </div>

          <div className="bg-[var(--clr-surface-card)] rounded-2xl shadow-2xl p-8">
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>First-time login:</strong> You're using a temporary password. Please create a new secure password to continue.
              </p>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handlePasswordChange} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-blue-500 transition-colors pr-12"
                    placeholder="Enter new password"
                    required
                    minLength={8}
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--clr-text-muted)] hover:text-[var(--clr-text-secondary)]"
                  >
                    {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-blue-500 transition-colors"
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password"
                />
              </div>

              <button
                type="submit"
                disabled={changingPassword}
                className="w-full py-3 bg-[var(--clr-brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--clr-brand-primary-hover)] focus:ring-4 focus:ring-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="change-password-submit"
              >
                {changingPassword ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <KeyRound className="w-5 h-5" />
                    Set New Password
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--clr-brand-primary)] rounded-2xl mb-4">
            <span className="text-3xl font-bold text-white">S</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">ScopeOps</h1>
          <p className="text-blue-200">Portfolio Visibility Platform</p>
        </div>

        {/* Login Card */}
        <div className="bg-[var(--clr-surface-card)] rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-bold text-[var(--clr-text-primary)] mb-6 text-center">Welcome Back</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-[var(--clr-sev-blocked)] bg-opacity-10 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-blue-500 transition-colors"
                placeholder="you@company.com"
                required
                autoComplete="email"
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-blue-500 transition-colors pr-12"
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--clr-text-muted)] hover:text-[var(--clr-text-secondary)]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-[var(--clr-brand-primary)] focus:ring-[var(--clr-brand-primary)]" />
                <span className="text-sm text-[var(--clr-text-secondary)]">Remember me</span>
              </label>
              <button type="button" className="text-sm text-[var(--clr-brand-primary)] hover:text-blue-700 font-medium">
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[var(--clr-brand-primary)] text-white rounded-lg font-medium hover:bg-[var(--clr-brand-primary-hover)] focus:ring-4 focus:ring-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="login-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-blue-200/60 text-sm mt-6">
          &copy; 2025 ScopeOps. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
